import { LitElement, html, css, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../services/api.js";
import "./cos-icon.js";

const BADGE_REFRESH_MS = 5 * 60 * 1000;

/** Feedback ticket 372, round 2 (Marcie, 2026-08-12): "I only have 7 at the
 *  moment and it's showing 9+" — round 1's fix (excluding awaiting_deploy/
 *  needs_decision below) only closed two of the active statuses that flip a
 *  report's `unread` flag with no actual content. EVERY status_change
 *  message is contentless (empty body — see model.FeedbackMessageKind's
 *  doc comment); it exists only to record that the pill moved, not to tell
 *  the reporter anything. And `needs_retest` is the ONLY status that
 *  genuinely asks the reporter to do something — every other status (open:
 *  not yet triaged; waiting: approved, queued, unclaimed; in_progress:
 *  someone has it; awaiting_deploy/needs_decision: their own on-screen copy
 *  says so already; reopened: the reporter's own "still broken" verdict,
 *  now back in the queue) means "the team has it, nothing needed from you
 *  right now" every bit as much as those first two did. So the badge is
 *  just `pendingRetest` — which is already exactly "needs_retest AND I'm
 *  watching it" (FeedbackService.hydrateListRows; a reporter always
 *  watches their own report) and doesn't depend on `unread` at all, so a
 *  report the reporter has already opened still counts until they actually
 *  submit a verdict. This is provably identical to what "Needs re-test
 *  only" already filters on (cos-feedback-panel.ts's `_mineRetestOnly`) —
 *  the two counts can never disagree again. */

/**
 * <cos-feedback-widget> — the always-mounted, eager header launcher for the
 * staff feedback / issue-capture platform.
 *
 * Deliberately tiny (badge count + a toggle) so it never grows the entry
 * bundle: the actual panel (tabs, capture toolbar, annotator) lives in
 * <cos-feedback-panel>, lazy-imported on first click — the same
 * `_agentMounted` dynamic-import pattern cos-app-shell already uses for the
 * admin agent panel.
 *
 * Renders nothing for anyone but Communico staff (userLevel === 1) — mirrors
 * the app-shell's own `${this.userLevel === 1 ? ... : ''}` gating for the
 * status pill, so the launcher self-gates even if a future call site
 * forgets the level check too.
 */
@customElement("cos-feedback-widget")
export class CosFeedbackWidget extends LitElement {
	@property({ type: Number }) userLevel = 1;
	@property({ type: Number }) userPartnerId: number | null = null;
	/** Mirrors cos-app-shell's own `collapsed` — the FAB anchors past the
	 *  live rail width (see `.fab-launcher`), so it needs to know when that
	 *  width drops to the collapsed 64px rail (a value the shared
	 *  `--cos-rail-width-active` token doesn't carry — that token only
	 *  tracks the compact/comfortable/wide preference). */
	@property({ type: Boolean, reflect: true }) collapsed = false;

	@state() private _open = false;
	@state() private _mounted = false;
	@state() private _badgeCount = 0;
	@state() private _chromeHidden = false;

	private _refreshTimer: ReturnType<typeof setInterval> | null = null;

	connectedCallback(): void {
		super.connectedCallback();
		if (this.userLevel === 1) {
			void this._refreshBadge();
			this._refreshTimer = setInterval(() => void this._refreshBadge(), BADGE_REFRESH_MS);
		}
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		if (this._refreshTimer) clearInterval(this._refreshTimer);
	}

	/** Badge = mine reports genuinely pending a re-test (`pendingRetest`) —
	 *  see the comment above; deliberately NOT `unread`, which flips on
	 *  every contentless status_change regardless of destination status.
	 *  Best-effort — a transient failure just leaves the previous count on
	 *  screen. */
	private async _refreshBadge(): Promise<void> {
		try {
			const r = await api.getMyFeedbackReports({ limit: 100 });
			this._badgeCount = r.data.filter((row) => row.pendingRetest).length;
		} catch {
			/* best-effort */
		}
	}

	private async _toggle(): Promise<void> {
		if (!this._mounted) {
			await import("./cos-feedback-panel.js");
			this._mounted = true;
		}
		this._open = !this._open;
		if (this._open) void this._refreshBadge();
	}

	/** The Cmd+Shift+F global shortcut (via cos-app-shell) calls this — a
	 *  no-op if already open. */
	open(): void {
		if (!this._open) void this._toggle();
	}

	/**
	 * Opens the panel already focused on a specific report — the re-test
	 * link's "come back to the report" leg. Driven by cos-app-shell reading
	 * `?retest_report=<id>` off the destination URL (services/retest-
	 * report.ts) once the shell — and therefore this widget — is actually
	 * mounted.
	 *
	 * Best-effort like open(): an id that fails to resolve (see
	 * cos-feedback-panel.focusReport — deleted, or not visible to this
	 * signed-in user) opens nothing rather than surfacing an error in a
	 * panel the person never asked to see.
	 */
	async openReport(id: number): Promise<void> {
		if (this.userLevel !== 1) return; // this component renders nothing otherwise
		if (!this._mounted) {
			await import("./cos-feedback-panel.js");
			this._mounted = true;
		}
		await this.updateComplete; // <cos-feedback-panel> now in the renderRoot
		const panel = this.renderRoot.querySelector("cos-feedback-panel");
		if (!panel) return;
		await panel.updateComplete;
		const opened = await panel.focusReport(id);
		if (opened) {
			this._open = true;
			void this._refreshBadge();
		}
	}

	private _onPanelClose = (): void => {
		this._open = false;
		void this._refreshBadge();
	};

	private _onChrome = (e: Event): void => {
		this._chromeHidden = (e as CustomEvent<{ hidden: boolean }>).detail.hidden;
	};

	static styles = css`
		:host {
			display: inline-flex;
		}
		/* Feedback #37 — the header launcher lives inside a plain, unpositioned
		 * header, so a module full-screen slide-in (e.g. cos-attend-registrants'
		 * scrim/panel at z-index 60/61) stacks a fixed-position layer above it —
		 * the button is still in the DOM but visually and pointer-wise buried.
		 * "Certain areas" is a CLASS of bug (any current or future module
		 * overlay), so the fix is a second, always-reachable affordance rather
		 * than trying to enumerate every overlay: a floating FAB that outranks
		 * every module overlay observed in the codebase (side panels top out at
		 * var(--cos-z-modal, 50); cos-attend-registrants is 60/61).
		 *
		 * Feedback report 370 — right:24/bottom:144 put it in the SAME corner every
		 * sidePanelManager-docked side panel (Discuss, Files, Reserve's
		 * "Edit booking", …) slides in from, so the FAB sat directly on top
		 * of whatever field that panel happened to render at that height —
		 * "obstructing the drop down menu ... gets in the way most of the
		 * time." Side panels dock RIGHT, so the FAB now anchors bottom-LEFT
		 * instead, past the live sidebar — never over the rail itself (that's
		 * the collapse-toggle's own corner) and never in the one corner every
		 * docked panel claims. The rail-width custom property is the same
		 * one cos-app-shell sizes its own grid column from, so the offset
		 * tracks compact/comfortable/wide live; the collapsed-host override
		 * below covers the icon-only 64px rail (that token only tracks the
		 * width preference, not the collapse toggle — see the collapsed
		 * property above); the <768px override covers the off-canvas
		 * drawer, where the rail claims no on-screen width at all. Kept
		 * clear of the admin-agent (right:24 bottom:24 z-index:95) and
		 * circulate-help (right:24 bottom:84 z-index:40) FABs by being on
		 * the opposite side, not by stacking. Hidden while the panel itself
		 * is open (its own scrim/panel already covers this same corner at
		 * z-index 95/96) so the FAB never
		 * floats on top of its own open panel. */
		.fab-launcher {
			position: fixed;
			left: calc(var(--cos-rail-width-active, 260px) + 24px);
			bottom: 24px;
			z-index: 98;
			width: 48px;
			height: 48px;
			border-radius: 50%;
			background: var(--cos-brand-700);
			color: var(--cos-surface);
			border: none;
			cursor: pointer;
			box-shadow: var(--cos-shadow-lg);
			display: inline-flex;
			align-items: center;
			justify-content: center;
			transition: background 150ms ease, transform 150ms ease;
		}
		:host([collapsed]) .fab-launcher {
			/* Collapsed rail is a fixed 64px, independent of
			   --cos-rail-width-active (see the property comment). */
			left: 88px;
		}
		@media (max-width: 768px) {
			.fab-launcher {
				/* The rail is an off-canvas drawer here (cos-app-shell's own
				   <768px override) — it claims no on-screen width to clear. */
				left: 24px;
			}
		}
		.fab-launcher:hover {
			background: var(--cos-brand-800);
		}
		@media (hover: hover) and (pointer: fine) {
			.fab-launcher:hover {
				transform: translateY(-1px);
			}
		}
		.fab-launcher:active {
			transform: scale(0.97);
		}
		.fab-launcher:focus-visible {
			outline: 2px solid var(--cos-brand-500);
			outline-offset: 3px;
		}
		@media (prefers-reduced-motion: reduce) {
			.fab-launcher { transition: none; }
			.fab-launcher:hover,
			.fab-launcher:active { transform: none; }
		}
		.launcher {
			position: relative;
			background: none;
			border: 1px solid var(--cos-border);
			border-radius: var(--cos-radius-sm);
			width: 40px;
			height: 40px;
			cursor: pointer;
			color: var(--cos-text-primary);
			display: inline-flex;
			align-items: center;
			justify-content: center;
		}
		.launcher:hover {
			background: var(--cos-surface-raised);
		}
		.launcher:focus-visible {
			outline: 2px solid var(--cos-brand-500);
			outline-offset: 2px;
		}
		.badge {
			position: absolute;
			top: -4px;
			right: -4px;
			background: var(--cos-status-danger-dot);
			color: var(--cos-text-on-color);
			font-size: var(--cos-font-size-2xs);
			font-weight: 700;
			min-width: 18px;
			height: 18px;
			border-radius: 999px;
			padding: 0 5px;
			display: inline-flex;
			align-items: center;
			justify-content: center;
			line-height: 1;
		}
	`;

	render() {
		if (this.userLevel !== 1) return nothing;
		// Feedback #37 — the floating FAB is the always-reachable escape
		// hatch: rendered whenever the panel itself is closed (so it never
		// floats on top of its own open panel, which covers this same
		// corner) and the chrome isn't mid-capture. It survives on top of
		// any module overlay via z-index (see the .fab-launcher comment).
		const showFab = !this._chromeHidden && !this._open;
		return html`
			${!this._chromeHidden
				? html`
						<button
							class="launcher"
							@click=${() => this._toggle()}
							aria-label="Send feedback"
							aria-haspopup="dialog"
							aria-expanded=${this._open ? "true" : "false"}
							title="Send feedback: report a bug or capture a screenshot"
						>
							<cos-icon name="bug" size="18"></cos-icon>
							${this._badgeCount > 0 ? html`<span class="badge">${this._badgeCount > 9 ? "9+" : this._badgeCount}</span>` : nothing}
						</button>
				  `
				: nothing}
			${showFab
				? html`
						<button
							class="fab-launcher"
							@click=${() => this._toggle()}
							aria-label="Send feedback: reachable from anywhere, including over open panels"
							aria-haspopup="dialog"
							title="Send feedback (⌘⇧F): reachable even with a panel or overlay open"
						>
							<cos-icon name="bug" size="20"></cos-icon>
							${this._badgeCount > 0 ? html`<span class="badge">${this._badgeCount > 9 ? "9+" : this._badgeCount}</span>` : nothing}
						</button>
				  `
				: nothing}
			${this._mounted
				? html`
						<cos-feedback-panel
							?open=${this._open}
							.userPartnerId=${this.userPartnerId}
							@panel-close=${this._onPanelClose}
							@feedback-chrome=${this._onChrome}
							@feedback-seen=${() => void this._refreshBadge()}
							@feedback-submitted=${() => void this._refreshBadge()}
						></cos-feedback-panel>
				  `
				: nothing}
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"cos-feedback-widget": CosFeedbackWidget;
	}
}
